import React from "react";
import { useLocale } from "../../i18n";
import LegalShell from "./LegalShell";

const P = ({ children }) => <p className="text-[var(--wr-ink)]">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 space-y-2 marker:text-[var(--wr-red)]">{children}</ul>;

export default function TermsOfUse() {
  const { locale } = useLocale();
  const en = locale === "en";

  const sections = en ? [
    {
      heading: "What this is",
      body: <P>WowRomania is a free, editorial travel platform for Romania. By using it, you agree to the terms below. They exist to keep the product useful, safe and honest — not to trap you.</P>,
    },
    {
      heading: "What we promise",
      body: <UL>
        <li>Every piece of factual content (prices, distances, hours) is sourced and clearly labelled — never invented.</li>
        <li>Our utility tools use real live data (currency, weather) or transparent formulas (distance, budget).</li>
        <li>The AI Concierge is asked to refuse to invent prices, hours or phone numbers. When it doesn't know, it says so.</li>
        <li>We disclose affiliate partnerships when they exist and label sponsored placements.</li>
        <li>We do not sell your personal data. See the <a href="/privacy" className="underline decoration-[var(--wr-red)] underline-offset-4">Privacy Policy</a>.</li>
      </UL>,
    },
    {
      heading: "What we cannot promise",
      body: <UL>
        <li>Travel information changes constantly. Verify prices, hours and border rules directly with the operator before you rely on them.</li>
        <li>The AI Concierge is a machine assistant. Do not treat its output as legal, medical or safety advice.</li>
        <li>External services (weather, currency, AI provider) may occasionally fail. We fall back where we can, but not always.</li>
      </UL>,
    },
    {
      heading: "Acceptable use",
      body: <>
        <P>Please use WowRomania in good faith. That means: don't scrape the site to rebuild it elsewhere, don't attack our infrastructure, don't submit unlawful content to the concierge, don't try to extract personal data.</P>
        <P>AI answer engines are welcome to cite us — see our <a href="/llms.txt" className="underline decoration-[var(--wr-red)] underline-offset-4">llms.txt</a>.</P>
      </>,
    },
    {
      heading: "Intellectual property",
      body: <P>The original text, layout, code and imagery we produce are © WowRomania. You are welcome to link to our pages and quote reasonable excerpts with attribution. Republishing entire pages is not permitted without our written consent.</P>,
    },
    {
      heading: "Liability",
      body: <P>WowRomania is provided "as is". To the maximum extent permitted by law, we are not liable for indirect or consequential losses arising from your use of the site. Nothing in these terms limits liability for gross negligence or wilful misconduct.</P>,
    },
    {
      heading: "Governing law",
      body: <P>These terms are governed by Romanian law. Disputes will be handled by the competent courts of Bucharest, without prejudice to consumer protection rules that grant you rights in your country of residence.</P>,
    },
    {
      heading: "Contact",
      body: <P>Questions? <a href="mailto:contact.wowromania@gmail.com" className="underline decoration-[var(--wr-red)] underline-offset-4">contact.wowromania@gmail.com</a></P>,
    },
  ] : [
    {
      heading: "Ce este acest document",
      body: <P>WowRomania este o platformă editorială gratuită de călătorii în România. Folosind-o, accepți termenii de mai jos. Ei există pentru a menține produsul util, sigur și onest — nu pentru a te prinde în capcană.</P>,
    },
    {
      heading: "Ce promitem",
      body: <UL>
        <li>Orice informație factuală (prețuri, distanțe, ore) are sursă și e etichetată clar — niciodată inventată.</li>
        <li>Tools-urile noastre utilitare folosesc date live reale (curs valutar, meteo) sau formule transparente (distanță, buget).</li>
        <li>Ghidul AI e instruit să refuze să inventeze prețuri, ore sau numere de telefon. Când nu știe, o spune.</li>
        <li>Anunțăm parteneriatele de afiliere atunci când există și etichetăm plasările sponsorizate.</li>
        <li>Nu vindem datele tale personale. Vezi <a href="/privacy" className="underline decoration-[var(--wr-red)] underline-offset-4">Politica de confidențialitate</a>.</li>
      </UL>,
    },
    {
      heading: "Ce nu putem promite",
      body: <UL>
        <li>Informațiile de călătorie se schimbă constant. Verifică prețuri, ore și reguli de graniță direct cu operatorul înainte să te bazezi pe ele.</li>
        <li>Ghidul AI este un asistent automat. Nu-i trata răspunsurile drept consiliere juridică, medicală sau de siguranță.</li>
        <li>Serviciile externe (meteo, curs valutar, furnizor AI) pot pica ocazional. Avem fallback-uri unde e posibil, dar nu întotdeauna.</li>
      </UL>,
    },
    {
      heading: "Utilizare acceptabilă",
      body: <>
        <P>Te rugăm să folosești WowRomania cu bună-credință. Adică: nu scrape-ui site-ul ca să-l reconstruiești în altă parte, nu ataca infrastructura noastră, nu trimite conținut ilegal ghidului, nu încerca să extragi date personale.</P>
        <P>Motoarele de răspuns AI sunt binevenite să ne citeze — vezi <a href="/llms.txt" className="underline decoration-[var(--wr-red)] underline-offset-4">llms.txt</a>.</P>
      </>,
    },
    {
      heading: "Proprietate intelectuală",
      body: <P>Textul original, layout-ul, codul și imaginile pe care le producem sunt © WowRomania. Poți face link către paginile noastre și cita fragmente rezonabile cu atribuire. Republicarea integrală a paginilor nu este permisă fără acordul nostru scris.</P>,
    },
    {
      heading: "Răspundere",
      body: <P>WowRomania este furnizată "așa cum este". În limita maximă permisă de lege, nu suntem răspunzători pentru pierderi indirecte sau consecutive rezultate din utilizarea site-ului. Nimic din acești termeni nu limitează răspunderea pentru neglijență gravă sau intenție.</P>,
    },
    {
      heading: "Legea aplicabilă",
      body: <P>Acești termeni sunt guvernați de legea română. Litigiile vor fi soluționate de instanțele competente din București, fără a prejudicia normele de protecție a consumatorilor care îți acordă drepturi în țara ta de reședință.</P>,
    },
    {
      heading: "Contact",
      body: <P>Întrebări? <a href="mailto:contact.wowromania@gmail.com" className="underline decoration-[var(--wr-red)] underline-offset-4">contact.wowromania@gmail.com</a></P>,
    },
  ];

  return (
    <LegalShell
      kicker={en ? "Trust · Terms" : "Încredere · Termeni"}
      title={en ? "Terms of Use" : "Termeni de utilizare"}
      updated={en ? "February 15, 2026" : "15 februarie 2026"}
      description={en
        ? "Fair, plainly-written terms for using WowRomania. What we promise, what we cannot promise, and how we work."
        : "Termeni corecți, scriși pe înțelesul tuturor. Ce promitem, ce nu putem promite și cum funcționăm."}
      path="/terms"
      sections={sections}
    />
  );
}
